<template>
  <div
    class="flex flex-col gap-6"
    style="scroll-margin-top: calc(4rem + var(--sat))"
  >
    <BaseCard class="md:hidden flex flex-col gap-y-5">
      <ShowcasesFormBanner />
      <div class="text-gray flex flex-col gap-y-9">
        <div class="leading-tight flex flex-col gap-y-5 items-start">
          <div class="text-xl font-bold">
            <div>
              {{ event?.name }}
            </div>
            <div>
              {{ eventDate }}
            </div>
          </div>

          <div
            v-if="untilDate"
            class="text-lg leading-tight w-full flex flex-nowrap items-center gap-x-3"
          >
            <BaseIcon class="shrink-0" name="ico_watch" size="md" />
            <div class="font-bold">
              Applications are accepted until
              {{ untilDate }}
            </div>
          </div>
        </div>
        <div
          v-if="descriptionMarked"
          class="text-lg marked showcase-form-description"
          v-html="descriptionMarked"
        />
      </div>
    </BaseCard>
    <BaseCard
      :class="!showcase?.is_show_form_participation_block && 'hidden md:block'"
    >
      <div class="md:py-8 md:space-y-10">
        <div
          class="hidden md:flex text-xl md:text-[22px] leading-tight flex-col md:flex-row gap-x-7 gap-y-2 items-start"
          :style="{ color: mainColor }"
        >
          <div class="flex-1">
            <div class="font-extrabold">
              {{ event?.name }}
            </div>
            <div>
              {{ eventDate }}
            </div>
          </div>

          <div
            v-if="untilDate"
            class="leading-tight w-full md:w-[330px] shrink-0 flex flex-nowrap items-center gap-x-3"
          >
            <BaseIcon class="shrink-0" name="ico_watch" size="md" />
            <div>
              <div>Applications are accepted</div>
              <div class="font-bold">until {{ untilDate }}</div>
            </div>
          </div>
        </div>

        <div
          v-if="descriptionMarked || showcase?.is_show_form_participation_block"
          class="flex gap-x-7"
        >
          <div
            v-if="descriptionMarked"
            class="hidden md:block w-full grow text-gray marked showcase-form-description large-info"
            v-html="descriptionMarked"
          />
          <div
            v-if="showcase?.is_show_form_participation_block"
            class="relative rounded-xl grow shrink md:min-w-[330px] md:px-9 md:py-7 overflow-hidden"
          >
            <div
              v-if="participationBlockDesktopColor"
              class="absolute inset-0 hidden md:block"
              :style="{ backgroundColor: participationBlockDesktopColor }"
            />
            <ShowcasesFormParticipation
              ref="participationFormComponent"
              class="relative"
              :disabled="props.disabled"
              @submit-request="onParticipationSubmit"
            />
          </div>
        </div>
      </div>
    </BaseCard>
  </div>
</template>

<script setup lang="ts">
import { useShowcases } from '~/stores/showcases'
import ShowcasesFormParticipation, {
  type ParticipationData,
} from '~/components/showcases/form/ShowcasesFormParticipation.vue'
import type { EventLite } from '~/models/events'

const { $marked } = useNuxtApp()
const { getHumanizedDatePeriod } = useDate()
const showcasesStore = useShowcases()

const { showcase, mainColor } = storeToRefs(showcasesStore)
const { getUntilDate } = showcasesStore

interface Props {
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
})

const emit = defineEmits<{
  (e: 'submitRequest', value: void): void
}>()

defineExpose({
  validateParticipation,
  getParticipationData,
})

const participationFormComponent = ref<InstanceType<
  typeof ShowcasesFormParticipation
> | null>(null)

const event = computed<EventLite | undefined>(() => showcase.value?.event)

const eventDate = computed<string>(() => {
  if (!event.value) {
    return ''
  }

  return (
    eventCustomDate.value ||
    getHumanizedDatePeriod(
      event.value.date_from,
      event.value.date_to,
      event.value.timezone,
      'en'
    )
  )
})

const eventCustomDate = computed<string>(() => {
  if (!event.value?.custom_date) {
    return ''
  }

  const parsedDate = parseFieldLocales(event.value.custom_date)
  return getFieldLocale(parsedDate || '', 'en')
})

const untilDate = computed<string>(() => {
  return showcase.value ? getUntilDate(showcase.value) : ''
})

const descriptionMarked = computed<string>(() => {
  return $marked(showcase.value?.form_description || '').replace(
    /<a href=/g,
    '<a target="_blank" href='
  )
})

const participationBlockDesktopColor = computed<string>(() => {
  return showcase.value?.form_participation_block_color || '#E4EFFD'
})

function onParticipationSubmit(): void {
  emit('submitRequest')
}

async function validateParticipation(): Promise<boolean> {
  if (showcase.value?.is_show_form_participation_block) {
    return (await participationFormComponent.value?.validate()) || false
  } else {
    return true
  }
}

function getParticipationData(): ParticipationData | null {
  return (
    (showcase.value?.is_show_form_participation_block &&
      participationFormComponent.value?.getParticipationData()) ||
    null
  )
}
</script>

<style lang="scss">
/* not scoped, because dynamic HTML insert */

.showcase-form-description {
  > p {
    padding: 0;
  }

  li:not(:last-child) {
    margin-bottom: 16px;
  }

  li {
    position: relative;
    list-style: none;
  }

  li::before {
    position: absolute;
    left: -1.5rem;
    top: 0;
    bottom: 0;
    margin: auto;
    content: '';
    background: #119df4;
    width: 12px;
    height: 12px;
    border: 1px solid #119df4;
    border-radius: 5px;
  }

  &.large-info {
    li::before {
      background: white !important;
    }
  }
}
</style>
